import React from "react";
import { CircleHelp, Monitor, Smartphone, Tablet, Trash2 } from "lucide-react";
import { FaAndroid, FaApple, FaChrome, FaLinux, FaWindows } from "react-icons/fa6";
import { SiMacos } from "react-icons/si";
import { Button } from "@/components/ui/button";
import { dateTime } from "./format";

const platforms = {
  apple: { icon: FaApple, label: "Apple", tone: "bg-slate-100 text-slate-700" },
  mac: { icon: SiMacos, label: "Mac", tone: "bg-slate-100 text-slate-700" },
  windows: { icon: FaWindows, label: "Windows", tone: "bg-sky-50 text-sky-700" },
  android: { icon: FaAndroid, label: "Android", tone: "bg-emerald-50 text-emerald-700" },
  chromeos: { icon: FaChrome, label: "ChromeOS", tone: "bg-amber-50 text-amber-700" },
  linux: { icon: FaLinux, label: "Linux", tone: "bg-slate-100 text-slate-700" },
  unknown: { icon: CircleHelp, label: "Unknown platform", tone: "bg-secondary text-muted-foreground" },
};
const kinds = {
  phone: { icon: Smartphone, label: "Phone" },
  tablet: { icon: Tablet, label: "Tablet" },
  computer: { icon: Monitor, label: "Computer" },
  unknown: { icon: CircleHelp, label: "Device type unknown" },
};

const RecognizedDevice = ({ device, disabled, onForget }) => {
  const platform = platforms[device.platform] || platforms.unknown;
  const kind = kinds[device.device_kind] || kinds.unknown;
  const PlatformIcon = platform.icon, KindIcon = kind.icon;
  return <div className="flex items-start gap-3 rounded-xl border border-border p-3" data-testid={`access-device-${device.id}`}>
    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${platform.tone}`} title={platform.label}>
      <PlatformIcon className="h-6 w-6" role="img" aria-label={platform.label} data-testid={`access-device-platform-icon-${device.id}`} />
    </span>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold [overflow-wrap:anywhere]" data-testid={`access-device-label-${device.id}`}>{device.label}</div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <span data-testid={`access-device-platform-${device.id}`}>{platform.label}</span>
        <span aria-hidden="true">·</span>
        <span className="inline-flex items-center gap-1.5" data-testid={`access-device-kind-${device.id}`}>
          <KindIcon className="h-3.5 w-3.5 shrink-0" role="img" aria-label={kind.label} data-testid={`access-device-kind-icon-${device.id}`} />
          {kind.label}
        </span>
      </div>
      <p className="mt-2 text-xs text-muted-foreground" data-testid={`access-device-dates-${device.id}`}>First trusted: {dateTime(device.created_at)}<br />Last used: {dateTime(device.last_used_at)}</p>
      {device.last_used_ip && <p className="mt-1 break-all text-[11px] text-muted-foreground" data-testid={`access-device-network-${device.id}`}>Last network: {device.last_used_ip}</p>}
    </div>
    <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0" title="Forget this device" aria-label={`Forget ${device.label}`} disabled={disabled} onClick={onForget} data-testid={`access-device-forget-${device.id}`}>
      <Trash2 className="h-4 w-4 text-destructive" />
    </Button>
  </div>;
};

export const RecognizedDevices = ({ devices, disabled, onAction }) => <section className="space-y-3" data-testid="access-detail-devices">
  <div className="flex flex-wrap items-center justify-between gap-2">
    <h3 className="font-semibold" data-testid="access-device-count">Recognized devices ({devices.length})</h3>
    {devices.length > 0 && <Button variant="outline" size="sm" onClick={() => onAction({ type: "revoke_all" })} disabled={disabled} data-testid="access-devices-forget-all"><Trash2 className="mr-2 h-4 w-4" />Forget all</Button>}
  </div>
  {devices.length === 0 && <p className="text-sm text-muted-foreground" data-testid="access-devices-empty">No remembered devices.</p>}
  {devices.map(device => <RecognizedDevice key={device.id} device={device} disabled={disabled} onForget={() => onAction({ type: "revoke_device", deviceId: device.id })} />)}
</section>;